/**
 * Web adapters for the stall detector (backendStall.ts): what urql and axios failures count as a stall, and the axios
 * retry policy built on it.
 */
import {
  backendStall, backoffDelay, classifyGraphQLErrors, classifyHttpStatus, isNonCriticalRequest,
  type StallDetector, type StallSignal,
} from './backendStall'

type CombinedErrorLike = {
  graphQLErrors?: { message?: string; extensions?: Record<string, any> }[]
  networkError?: unknown
  response?: { status?: number } | null
}

export function graphQLStallSignal(error: CombinedErrorLike | null | undefined): StallSignal | null {
  if (!error) return null
  return classifyGraphQLErrors(error.graphQLErrors)
    ?? classifyHttpStatus(error.response?.status)
}

export function reportGraphQLStall(error: CombinedErrorLike | null | undefined, detector: StallDetector = backendStall) {
  const signal = graphQLStallSignal(error)
  if (signal) detector.report(signal)
}

type AxiosErrorLike = {
  code?: string
  name?: string
  message?: string
  response?: { status?: number; data?: any } | null
  config?: { method?: string; url?: string; baseURL?: string } | null
}

/** Our API (relative URLs on axios' baseURL). Third-party calls (maps, the maintenance flag Worker) say nothing about our DB. */
const isOwnBackend = (config: AxiosErrorLike['config']) => {
  const url = config?.url ?? ''
  return !/^https?:\/\//i.test(url) || (!!config?.baseURL && url.startsWith(config.baseURL))
}

export function axiosStallSignal(error: AxiosErrorLike | null | undefined): StallSignal | null {
  if (!error || !isOwnBackend(error.config)) return null
  const data = error.response?.data
  return classifyHttpStatus(error.response?.status)
    ?? (Array.isArray(data?.errors) ? classifyGraphQLErrors(data.errors) : null)
    // Functions that relay a Hasura failure answer 500 with its message in `error` or `message`.
    ?? classifyGraphQLErrors([{ message: typeof data?.error === 'string' ? data.error : data?.message }])
}

export function reportAxiosStall(error: AxiosErrorLike | null | undefined, detector: StallDetector = backendStall) {
  const signal = axiosStallSignal(error)
  if (signal) detector.report(signal)
}


/**
 * Global axios-retry condition. Retries only requests that never reached the server (offline, DNS, connection
 * reset; not a timeout, which may still be running there), for any method, as before: drivers in dead zones rely on
 * their POSTs going out once the signal is back. A stalled DB answers (5xx, statement timeout), so those are never
 * retried here and the stall is not amplified. While the backend is stalled, non-critical requests (reports, exports,
 * dashboards, history) are not retried at all.
 */
export function shouldRetryRequest(error: AxiosErrorLike, detector: Pick<StallDetector, 'isStalled'> = backendStall): boolean {
  if (!error || error.response) return false
  if (error.code === 'ECONNABORTED' || error.code === 'ETIMEDOUT') return false
  const config = error.config ?? {}
  if (detector.isStalled() && isNonCriticalRequest(config.url)) return false
  return true
}

/** axios-retry delay: exponential, full jitter, 60 s cap; a higher base while stalled. */
export const retryDelayMs = (retryCount: number, detector: Pick<StallDetector, 'isStalled'> = backendStall) =>
  backoffDelay(retryCount, detector.isStalled())
